import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Divider,
  CardActions,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { motion } from 'framer-motion';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getBlogPosts, subscribeToNewsletter, likePost, dislikePost } from '../../../services/api';
import { useNavigate } from 'react-router-dom';

const NewsAndBlogs = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [likedPosts, setLikedPosts] = useState({});
  const [dislikedPosts, setDislikedPosts] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await getBlogPosts();
      setBlogPosts(response.data.results);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch blog posts');
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      await subscribeToNewsletter(email);
      setSnackbar({
        open: true,
        message: 'Successfully subscribed to newsletter!',
        severity: 'success',
      });
      setEmail('');
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to subscribe. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleLike = async (slug) => {
    try {
      const response = await likePost(slug);
      const { status, likes_count, dislikes_count } = response.data;
      
      // Update the post in the list with new counts
      setBlogPosts(prevPosts => 
        prevPosts.map(post => 
          post.slug === slug 
            ? { 
                ...post, 
                likes_count, 
                dislikes_count 
              }
            : post
        )
      );

      // Update liked state
      setLikedPosts(prev => ({
        ...prev,
        [slug]: status === 'liked'
      }));

      // Update disliked state
      setDislikedPosts(prev => ({
        ...prev,
        [slug]: false
      }));

    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to like post. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleDislike = async (slug) => {
    try {
      const response = await dislikePost(slug);
      const { status, likes_count, dislikes_count } = response.data;
      
      // Update the post in the list with new counts
      setBlogPosts(prevPosts => 
        prevPosts.map(post => 
          post.slug === slug 
            ? { 
                ...post, 
                likes_count, 
                dislikes_count 
              }
            : post
        )
      );

      // Update disliked state
      setDislikedPosts(prev => ({
        ...prev,
        [slug]: status === 'disliked'
      }));

      // Update liked state
      setLikedPosts(prev => ({
        ...prev,
        [slug]: false
      }));

    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to dislike post. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ pt: { xs: '120px', md: '140px' } }}>
      {/* Video Section */}
      <Box sx={{ 
        position: 'relative',
        height: { xs: '300px', md: '500px' },
        mb: 6,
        overflow: 'hidden',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Box
          component="video"
          src="/videos/ad.mp4"
          controls
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Page Title */}
      <Container sx={{ mb: 1 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 700,
            mb: 2,
            color: 'primary.main',
          }}
        >
          News & Blogs
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{
            maxWidth: '800px',
          }}
        >
          Stay updated with the latest insights, updates, and stories from the world of geospatial technology
        </Typography>
      </Container>

      {/* Blog Posts Section */}
      <Box sx={{ py: 4, backgroundColor: '#f5f5f5' }}>
        <Container>
          <Grid container spacing={4}>
            {blogPosts.map((post, index) => (
              <Grid item xs={12} md={6} key={post.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                >
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="240"
                      image={post.image}
                      alt={post.title}
                    />
                    <CardContent>
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Chip
                          icon={<CategoryIcon />}
                          label={post.category}
                          color="primary"
                          size="small"
                        />
                        <Chip
                          icon={<AccessTimeIcon />}
                          label={new Date(post.date).toLocaleDateString()}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                      <Typography variant="h5" gutterBottom>
                        {post.title}
                      </Typography>
                      <Typography variant="body1" paragraph>
                        {post.excerpt}
                      </Typography>
                      
                      {/* Metrics Section */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Tooltip title="Views">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <VisibilityIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {post.views}
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Tooltip title="Likes">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ThumbUpIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {post.likes_count}
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Tooltip title="Dislikes">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ThumbDownIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {post.dislikes_count}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Box>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                      <Box>
                        <Tooltip title="Like">
                          <IconButton 
                            onClick={() => handleLike(post.slug)} 
                            size="small"
                            color={likedPosts[post.slug] ? "primary" : "default"}
                          >
                            <ThumbUpIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Dislike">
                          <IconButton 
                            onClick={() => handleDislike(post.slug)} 
                            size="small"
                            color={dislikedPosts[post.slug] ? "primary" : "default"}
                          >
                            <ThumbDownIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Button
                        endIcon={<ArrowForwardIcon />}
                        color="primary"
                        sx={{ textTransform: 'none' }}
                        onClick={() => navigate(`/news/${post.slug}`)}
                      >
                        Read More
                      </Button>
                    </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Newsletter Section */}
      <Box sx={{ py: 6, backgroundColor: '#fff' }}>
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom>
                Stay Updated
              </Typography>
              <Typography variant="body1" paragraph>
                Subscribe to our newsletter to receive the latest updates, insights, and stories from the world of geospatial technology.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="form"
                onSubmit={handleSubscribe}
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: '200px' }}>
                  <TextField
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                      },
                    }}
                  />
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ minWidth: '150px' }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewsAndBlogs; 